import {
  Badge,
  Logo,
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@osteopro/ui';
import { Rocket } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboardingStatus } from '../../hooks/api/use-onboarding.js';
import { allRequiredStepsDone } from '../../onboarding/mandatory.js';
import { checklistProgress } from '../../onboarding/steps.js';
import { useOnboardingScope } from '../../onboarding/use-onboarding-scope.js';
import { OnboardingChecklist } from './OnboardingChecklist.js';
import { OnboardingHideButton } from './OnboardingHideButton.js';

/**
 * Entrée « Mise en route » de la barre latérale, avec sa pastille de progression. Elle ouvre la
 * liste dans un panneau, sans quitter l'écran. Son propre groupe la place au-dessus du titre
 * « Espace de travail ». Présente tant que la liste n'est ni terminée ni masquée ; avant la
 * réponse du serveur ou en cas d'échec, rien — la carte du tableau de bord dit l'erreur, la barre
 * latérale ne la répète pas.
 */
export function OnboardingNavItem({ onNavigate }: { onNavigate: (url: string) => void }) {
  const { t } = useTranslation();
  const inScope = useOnboardingScope();
  const status = useOnboardingStatus(inScope);
  const [open, setOpen] = useState(false);

  const data = status.data;
  if (!inScope || data === undefined || data.checklistHidden || data.steps.length === 0) {
    return null;
  }
  const progress = checklistProgress(data.steps);
  if (progress.complete) return null;

  const label = t('onboarding:nav.label');

  return (
    <SidebarGroup>
      <SidebarMenu className="gap-1">
        <SidebarMenuItem>
          <SidebarMenuButton
            onClick={() => {
              setOpen(true);
            }}
            tooltip={label}
            data-testid="nav-onboarding"
          >
            <Rocket className="h-5 w-5" />
            <span className="font-medium">{label}</span>
            <Badge
              variant="secondary"
              className="ml-auto h-4 py-0 text-[10px] tabular-nums"
              aria-label={t('onboarding:nav.progress', {
                done: progress.done,
                total: progress.total,
              })}
              data-testid="badge-onboarding-progress"
            >
              {progress.done}/{progress.total}
            </Badge>
          </SidebarMenuButton>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetContent
              className="w-full overflow-y-auto sm:max-w-lg"
              data-testid="sheet-onboarding"
            >
              <SheetHeader>
                <Logo className="mx-auto h-12 w-12" title={t('common:app.name')} />
                <SheetTitle>{t('onboarding:checklist.title')}</SheetTitle>
                <SheetDescription>{t('onboarding:checklist.description')}</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <OnboardingChecklist
                  status={data}
                  onOpen={(path) => {
                    setOpen(false);
                    onNavigate(path);
                  }}
                />
                {allRequiredStepsDone(data.steps) && (
                  <div className="flex justify-end">
                    <OnboardingHideButton
                      onHidden={() => {
                        setOpen(false);
                      }}
                    />
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarGroup>
  );
}
